import { usePageRefreshKey } from '../../../../context/PageRefreshContext';
import { localDateString } from '../../../../utils/helpers/localDate';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { getGlAccountsOpening, createAccountsOpening } from '../../../../redux/actions/SocietyPortal/AccountsOpeningAction/AccountsOpeningAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { toast } from 'react-toastify';
import { financialYears } from '../../../../consts/DropDownOptions';
import SearchableDropdown from '../../../../components/SearchableDropdown';

const getCurrentFY = () => {
    const now = new Date();
    const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${year}-${year + 1}`;
};

const Opening = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    const { glAccountsData, glLoading, createLoading } = useSelector(state => state.AccountsOpeningReducer);

    const [balances, setBalances] = useState({});
    const [financialYear, setFinancialYear] = useState(getCurrentFY());
    const [asOfDate, setAsOfDate] = useState(localDateString());

    // Fetch GL accounts with opening balance for selected FY
    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (!societyId) return;
        dispatch(getGlAccountsOpening({ societyId, year: financialYear }));
    }, [dispatch, societyId, financialYear, refreshKey]);

    // Map API response
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const accounts = glAccountsData?.accounts || [];
    const openingEntryDate = glAccountsData?.entry_date || null;

    // Populate balances from API response
    useEffect(() => {
        if (accounts.length > 0) {
            const initial = {};
            accounts.forEach(acc => {
                const dr = parseFloat(acc.dr_amount) || 0;
                const cr = parseFloat(acc.cr_amount) || 0;
                initial[acc.account_id] = {
                    debit: dr > 0 ? dr.toString() : '',
                    credit: cr > 0 ? cr.toString() : '',
                };
            });
            setBalances(initial);
            if (openingEntryDate) {
                setAsOfDate(openingEntryDate);
            }
        } else {
            setBalances({});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accounts, openingEntryDate]);

    const handleChange = (accountId, field, value) => {
        if (value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return;
        setBalances(prev => ({
            ...prev,
            [accountId]: {
                ...prev[accountId],
                [field]: value,
                ...(field === 'debit' && value ? { credit: '' } : {}),
                ...(field === 'credit' && value ? { debit: '' } : {}),
            }
        }));
    };

    const totals = useMemo(() => {
        let totalDebit = 0;
        let totalCredit = 0;
        Object.values(balances).forEach(b => {
            totalDebit += parseFloat(b.debit) || 0;
            totalCredit += parseFloat(b.credit) || 0;
        });
        const difference = Math.abs(totalDebit - totalCredit);
        return { totalDebit, totalCredit, difference, isBalanced: difference < 0.01 };
    }, [balances]);

    const formatCurrency = (amount) => parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const handleDownload = () => {
        if (accounts.length === 0) {
            toast.warn('No accounts to download');
            return;
        }
        const headers = ['Account Name', 'Account Code', 'Debit', 'Credit'];
        const rows = accounts.map(acc => {
            const b = balances[acc.account_id] || {};
            return [
                `"${acc.name}"`,
                acc.ac_code,
                parseFloat(b.debit) || 0,
                parseFloat(b.credit) || 0,
            ].join(',');
        });
        rows.push(['', '', formatCurrency(totals.totalDebit), formatCurrency(totals.totalCredit)].join(','));
        const csv = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `opening_balance_${financialYear}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleSave = (status) => {
        const registers = [];
        Object.entries(balances).forEach(([accountId, b]) => {
            const dr = parseFloat(b.debit) || 0;
            const cr = parseFloat(b.credit) || 0;
            if (dr > 0 || cr > 0) {
                const acc = accounts.find(a => a.account_id === parseInt(accountId));
                if (acc) {
                    registers.push({ account_id: acc.account_id, account_cd: acc.ac_code, dr_amount: dr, cr_amount: cr });
                }
            }
        });
        if (registers.length === 0) {
            toast.warn('Please enter at least one debit or credit amount');
            return;
        }
        if (status === 'Submit' && !totals.isBalanced) {
            toast.warn('Total debits must equal total credits before submitting');
            return;
        }
        dispatch(createAccountsOpening({
            societyId,
            entry_date: asOfDate,
            year: financialYear,
            status,
            registers,
            onSuccess: () => {
                dispatch(getGlAccountsOpening({ societyId, year: financialYear }));
            },
        }));
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-5">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <div className="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-gradient-to-br tw-from-blue-500 tw-to-indigo-600 tw-flex tw-items-center tw-justify-center tw-shadow-lg tw-shadow-blue-500/20">
                        <Icon icon="mdi:book-open-page-variant" className="tw-text-xl tw-text-white" />
                    </div>
                    <div>
                        <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Opening Balance</h1>
                        <p className="tw-text-[11px] tw-text-gray-400">Set initial balances for your accounts</p>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    {/* Financial Year */}
                    <SearchableDropdown
                        value={financialYear}
                        onChange={setFinancialYear}
                        options={financialYears}
                        placeholder="Financial Year"
                        clearable={false}
                        small
                    />

                    {/* Download */}
                    <button
                        onClick={handleDownload}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-red-600 tw-bg-white tw-border tw-border-red-200 tw-rounded-lg hover:tw-bg-red-50 tw-shadow-sm tw-transition-all"
                    >
                        <Icon icon="mdi:download-outline" className="tw-text-sm" />
                        Download
                    </button>

                    {/* As of Date */}
                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-px-3 tw-py-2 tw-shadow-sm">
                        <span className="tw-text-xs tw-text-gray-500 tw-font-medium">As of Date:</span>
                        <input
                            type="date"
                            value={asOfDate}
                            onChange={(e) => setAsOfDate(e.target.value)}
                            className="tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-transparent tw-border-none tw-outline-none tw-cursor-pointer"
                        />
                    </div>

                    {/* Save as Draft */}
                    <button
                        onClick={() => handleSave('Draft')}
                        disabled={createLoading}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-50 tw-shadow-sm tw-transition-all disabled:tw-opacity-50"
                    >
                        {createLoading ? 'Saving...' : 'Save as Draft'}
                    </button>

                    {/* Submit */}
                    <button
                        onClick={() => handleSave('Submit')}
                        disabled={createLoading}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-white tw-bg-red-600 tw-rounded-lg hover:tw-bg-red-700 tw-shadow-sm tw-transition-all disabled:tw-opacity-50"
                    >
                        {createLoading ? 'Submitting...' : 'Submit'}
                    </button>
                </div>
            </div>

            {/* Summary Card */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-4 tw-mb-4 tw-shadow-sm">
                <div className="tw-flex tw-items-center tw-gap-8">
                    <div>
                        <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-medium mb-0">Total Debit</p>
                        <p className="tw-text-lg tw-font-bold tw-text-gray-800 tw-mt-0.5 mb-0">
                            <span className="tw-text-sm tw-font-normal tw-text-gray-400 tw-mr-0.5">&#8377;</span>
                            {formatCurrency(totals.totalDebit)}
                        </p>
                    </div>
                    <div>
                        <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-medium mb-0">Total Credit</p>
                        <p className="tw-text-lg tw-font-bold tw-text-gray-800 tw-mt-0.5 mb-0">
                            <span className="tw-text-sm tw-font-normal tw-text-gray-400 tw-mr-0.5">&#8377;</span>
                            {formatCurrency(totals.totalCredit)}
                        </p>
                    </div>
                    <div>
                        <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-medium mb-0">Difference</p>
                        <div className="tw-flex tw-items-center tw-gap-2 tw-mt-0.5">
                            <p className={`tw-text-lg tw-font-bold ${totals.isBalanced ? 'tw-text-emerald-600' : 'tw-text-red-600'} mb-0`}>
                                <span className="tw-text-sm tw-font-normal tw-mr-0.5">&#8377;</span>
                                {formatCurrency(totals.difference)}
                            </p>
                            {totals.isBalanced && (
                                <Icon icon="mdi:check-circle" className="tw-text-emerald-500 tw-text-lg" />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Info Note */}
            <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-xl tw-p-4 tw-mb-4">
                <div className="tw-flex tw-items-start tw-gap-2">
                    <Icon icon="mdi:information-outline" className="tw-text-blue-500 tw-text-base tw-mt-0.5 tw-flex-shrink-0" />
                    <p className="tw-text-xs tw-text-blue-700 tw-leading-relaxed mb-0">
                        <span className="tw-font-semibold">Note:</span> Opening balances are used to set the initial balances for your accounts at the start of your accounting period. Make sure total debits equal total credits to maintain balance.
                    </p>
                </div>
            </div>

            {/* Table */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
                <table className="tw-w-full">
                    <thead>
                        <tr className="tw-border-b tw-border-gray-100 tw-bg-gray-50/50">
                            <th className="tw-text-left tw-px-5 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-[40%]">Account</th>
                            <th className="tw-text-left tw-px-5 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-[20%]">Account Code</th>
                            <th className="tw-text-right tw-px-5 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-[20%]">Debit (&#8377;)</th>
                            <th className="tw-text-right tw-px-5 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-[20%]">Credit (&#8377;)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {glLoading ? (
                            <tr>
                                <td colSpan="4" className="tw-text-center tw-py-20">
                                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                                        <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-blue-50 tw-flex tw-items-center tw-justify-center tw-animate-pulse">
                                            <div className="tw-animate-spin tw-rounded-full tw-h-5 tw-w-5 tw-border-2 tw-border-blue-200 tw-border-t-blue-600"></div>
                                        </div>
                                        <span className="tw-text-gray-400 tw-text-xs">Loading accounts...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : accounts.length > 0 ? (
                            accounts.map(acc => (
                                <tr key={acc.account_id} className="tw-border-b tw-border-gray-50 hover:tw-bg-gray-50/50 tw-transition-colors">
                                    <td className="tw-px-5 tw-py-3">
                                        <div className="tw-flex tw-items-center tw-gap-2">
                                            <span className="tw-text-xs tw-font-medium tw-text-gray-700">{acc.name}</span>
                                            {acc.is_member_account && (
                                                <span className="tw-text-[9px] tw-text-blue-600 tw-bg-blue-50 tw-px-1.5 tw-py-0.5 tw-rounded tw-font-medium tw-border tw-border-blue-100">Member</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="tw-px-5 tw-py-3">
                                        <code className="tw-text-[11px] tw-text-gray-500 tw-bg-gray-50 tw-px-1.5 tw-py-0.5 tw-rounded tw-font-medium">{acc.ac_code}</code>
                                    </td>
                                    <td className="tw-px-5 tw-py-3">
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="0.00"
                                            value={balances[acc.account_id]?.debit || ''}
                                            onChange={(e) => handleChange(acc.account_id, 'debit', e.target.value)}
                                            className="tw-w-full tw-text-right tw-text-xs tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg focus:tw-outline-none focus:tw-border-blue-400 focus:tw-ring-1 focus:tw-ring-blue-100 tw-transition-all"
                                        />
                                    </td>
                                    <td className="tw-px-5 tw-py-3">
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="0.00"
                                            value={balances[acc.account_id]?.credit || ''}
                                            onChange={(e) => handleChange(acc.account_id, 'credit', e.target.value)}
                                            className="tw-w-full tw-text-right tw-text-xs tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg focus:tw-outline-none focus:tw-border-blue-400 focus:tw-ring-1 focus:tw-ring-blue-100 tw-transition-all"
                                        />
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" className="tw-text-center tw-py-20">
                                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                        <div className="tw-w-14 tw-h-14 tw-rounded-2xl tw-bg-gray-50 tw-flex tw-items-center tw-justify-center">
                                            <Icon icon="mdi:book-open-page-variant" className="tw-text-3xl tw-text-gray-300" />
                                        </div>
                                        <p className="tw-text-gray-400 tw-text-xs tw-font-medium">No accounts found</p>
                                        <p className="tw-text-gray-300 tw-text-[10px]">Set up your chart of accounts first</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                    {accounts.length > 0 && (
                        <tfoot>
                            <tr className="tw-border-t-2 tw-border-gray-200 tw-bg-gray-50/80">
                                <td className="tw-px-5 tw-py-3 tw-text-xs tw-font-bold tw-text-gray-700" colSpan="2">Total</td>
                                <td className="tw-px-5 tw-py-3 tw-text-right tw-text-xs tw-font-bold tw-text-emerald-600">
                                    &#8377; {formatCurrency(totals.totalDebit)}
                                </td>
                                <td className="tw-px-5 tw-py-3 tw-text-right tw-text-xs tw-font-bold tw-text-red-600">
                                    &#8377; {formatCurrency(totals.totalCredit)}
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
};

export default Opening;
